import type {
  RegulationReason,
  RegulationSettingRow,
} from "../api/regulationSettingsApi"

export const DEFAULT_REASON: RegulationReason = "legal_obligation"

export interface SelectionEntry {
  reason: RegulationReason
  requiredSince: string | null
}

export type SelectionMap = Record<string, SelectionEntry>

/** The set of rows subscribed true on load — explicit or inferred, so the
 *  first save promotes an inferred set into explicit rows unchanged. */
export function initialRegulationSelection(
  rows: RegulationSettingRow[],
): SelectionMap {
  const map: SelectionMap = {}
  for (const row of rows) {
    if (row.regulationId && row.subscribed) {
      map[row.regulationId] = {
        reason: row.reason ?? DEFAULT_REASON,
        requiredSince: row.requiredSince ?? null,
      }
    }
  }
  return map
}

/**
 * Pure coupling logic: selecting a row also selects every regulation it
 * `requires`, transitively. Unselecting it cascades to every currently-selected
 * row whose requirement is no longer satisfied, repeated to a fixed point so a
 * multi-level chain unwinds fully.
 */
export function toggleRegulationSelection(
  rows: RegulationSettingRow[],
  selection: SelectionMap,
  row: RegulationSettingRow,
  checked: boolean,
): SelectionMap {
  if (!row.regulationId) return selection
  const byName = new Map(rows.map((r) => [r.name, r]))
  const next: SelectionMap = { ...selection }

  if (checked) {
    next[row.regulationId] = next[row.regulationId] ?? {
      reason: row.reason ?? DEFAULT_REASON,
      requiredSince: row.requiredSince ?? null,
    }
    const queue = [...row.requires]
    while (queue.length > 0) {
      const name = queue.shift()!
      const reqRow = byName.get(name)
      if (!reqRow?.regulationId || next[reqRow.regulationId]) continue
      next[reqRow.regulationId] = {
        reason: reqRow.reason ?? DEFAULT_REASON,
        requiredSince: reqRow.requiredSince ?? null,
      }
      queue.push(...reqRow.requires)
    }
    return next
  }

  delete next[row.regulationId]
  let changed = true
  while (changed) {
    changed = false
    for (const other of rows) {
      if (!other.regulationId || !(other.regulationId in next)) continue
      const satisfied = other.requires.every((reqName) => {
        const reqRow = byName.get(reqName)
        return reqRow?.regulationId ? reqRow.regulationId in next : true
      })
      if (!satisfied) {
        delete next[other.regulationId]
        changed = true
      }
    }
  }
  return next
}

/** The loaded selection closed over `requires`: a stored subscription set can
 *  hold PFAS without REACH_ANNEX_XVII (rows written before this screen
 *  existed), and the PUT refuses that. Selecting the prerequisites up front
 *  makes the screen dirty, so the user sees what the save will add. */
export function withRequiredRegulations(
  rows: RegulationSettingRow[],
  selection: SelectionMap,
): SelectionMap {
  let next = selection
  for (const row of rows) {
    if (row.regulationId && selection[row.regulationId]) {
      next = toggleRegulationSelection(rows, next, row, true)
    }
  }
  return next
}
